import React from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Scale, Clock, Users, Globe, Save, Loader2 } from 'lucide-react';

const RCCTab = ({ orgForm, setOrgForm, handleOrgSave, saving }) => {
  return (
    <Card className="border-[#E2E6EA]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-[#00A3A3]" />
          RCC By-Law Overrides
        </CardTitle>
        <CardDescription>
          Configure defaults per the Revised Corporation Code (RA 11232). Each setting shows the RCC default but can be overridden per your by-laws.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Notice Periods */}
        <div>
          <h4 className="font-medium text-[#1A1D21] mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#F58A26]" />Notice Periods
          </h4>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Board Notice (days)</Label>
              <Input type="number" min={1} value={orgForm.bylaw_board_notice_days} onChange={(e) => setOrgForm({ ...orgForm, bylaw_board_notice_days: parseInt(e.target.value) || 7 })} className="border-[#E2E6EA]" data-testid="board-notice-days" />
              <p className="text-xs text-[#5A6570]">RCC default: 7 days</p>
            </div>
            <div className="space-y-2">
              <Label>Regular ASM Notice (days)</Label>
              <Input type="number" min={1} value={orgForm.bylaw_asm_notice_days} onChange={(e) => setOrgForm({ ...orgForm, bylaw_asm_notice_days: parseInt(e.target.value) || 21 })} className="border-[#E2E6EA]" data-testid="asm-notice-days" />
              <p className="text-xs text-[#5A6570]">RCC Sec 50: at least 21 days</p>
            </div>
            <div className="space-y-2">
              <Label>Special Meeting Notice (days)</Label>
              <Input type="number" min={1} value={orgForm.bylaw_ssm_notice_days} onChange={(e) => setOrgForm({ ...orgForm, bylaw_ssm_notice_days: parseInt(e.target.value) || 7 })} className="border-[#E2E6EA]" data-testid="ssm-notice-days" />
              <p className="text-xs text-[#5A6570]">RCC: at least 1 week for special meetings</p>
            </div>
          </div>
        </div>

        {/* Quorum Rules */}
        <div>
          <h4 className="font-medium text-[#1A1D21] mb-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-[#F58A26]" />Quorum Rules
          </h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Board Quorum</Label>
              <Select value={orgForm.bylaw_board_quorum_type} onValueChange={(value) => setOrgForm({ ...orgForm, bylaw_board_quorum_type: value })}>
                <SelectTrigger className="border-[#E2E6EA]" data-testid="board-quorum-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="majority_of_members">Majority of Members (RCC Sec 53)</SelectItem>
                  <SelectItem value="two_thirds">Two-thirds of Members</SelectItem>
                  <SelectItem value="custom">Custom (per by-laws)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Stockholders Quorum</Label>
              <Select value={orgForm.bylaw_stockholders_quorum_type} onValueChange={(value) => setOrgForm({ ...orgForm, bylaw_stockholders_quorum_type: value })}>
                <SelectTrigger className="border-[#E2E6EA]" data-testid="stockholders-quorum-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="majority_of_outstanding_capital_stock">Majority of Outstanding Capital Stock (RCC Sec 51)</SelectItem>
                  <SelectItem value="two_thirds_outstanding">Two-thirds of Outstanding Capital Stock</SelectItem>
                  <SelectItem value="custom">Custom (per by-laws)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Board Meeting Frequency */}
        <div>
          <h4 className="font-medium text-[#1A1D21] mb-3 flex items-center gap-2">
            <Globe className="h-4 w-4 text-[#F58A26]" />Board Meeting Frequency
          </h4>
          <Select value={orgForm.bylaw_board_meeting_frequency} onValueChange={(value) => setOrgForm({ ...orgForm, bylaw_board_meeting_frequency: value })}>
            <SelectTrigger className="border-[#E2E6EA] max-w-sm" data-testid="board-meeting-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly (RCC Sec 53 default)</SelectItem>
              <SelectItem value="quarterly">Quarterly (if by-laws provide)</SelectItem>
              <SelectItem value="bimonthly">Bi-monthly</SelectItem>
              <SelectItem value="custom">Custom schedule</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-[#5A6570] mt-1">
            RCC Sec 53: Regular meetings of directors shall be held monthly, unless otherwise provided in the by-laws
          </p>
        </div>

        {/* RCC References */}
        <div className="p-4 bg-[#00A3A3]/5 border border-[#00A3A3]/20 rounded-lg space-y-2">
          <h4 className="font-medium text-[#1A1D21]">Key RCC References</h4>
          <ul className="text-xs text-[#5A6570] space-y-1">
            <li><strong>Sec 49:</strong> Regular/special meetings of stockholders at principal office or via remote communication</li>
            <li><strong>Sec 50:</strong> Written notice at least 21 days (regular) or 1 week (special) before stockholders meeting</li>
            <li><strong>Sec 51:</strong> Quorum = majority of outstanding capital stock (stockholders)</li>
            <li><strong>Sec 53:</strong> Board meets monthly; quorum = majority of directors as stated in AOI</li>
            <li><strong>Sec 24:</strong> Corporate Secretary must be a Filipino citizen and resident</li>
            <li><strong>Sec 73:</strong> Corporate Secretary shall keep the minutes of all meetings</li>
            <li><strong>SEC MC 6-2020:</strong> Guidelines for remote stockholders meetings</li>
          </ul>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleOrgSave} disabled={saving} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-rcc-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RCCTab;
